// TODO: Codes related to showing flag is commented because this view is currently wrong and asked to hide this view and keep code for future
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import type { ActiveTrainState, SimpleBounds, TrainRenderData } from '@/types';
import type { Layer } from '@deck.gl/core';

import {
  MIN_POOL_CAPACITY,
  POOL_BUFFER_SIZE,
  POOL_CLEANUP_INTERVAL_FRAMES,
} from '@/utils/constants/map';
import { getNextZoomBucket } from '@/utils/helpers/train-simulation/get-next-zoom-bucket';
import { getTrainRenderInterval } from '@/utils/helpers/train-simulation/get-train-render-interval';
import { processTrainFrame } from '@/utils/helpers/train-simulation/process-train-frame';
import { getTrainDeckLayers } from '@/utils/helpers/train-simulation/train-deck-layers';

interface UseTrainAnimationLoopProps {
  mapExists: boolean;
  trainStateMapRef: RefObject<Map<string, ActiveTrainState>>;
  visibleTrainIdsRef: RefObject<string[]>;
  visibleBoundsRef: RefObject<SimpleBounds | null>;
  currentZoomRef: RefObject<number>;
  lockedIdRef: RefObject<string | number | null>;
  isInteractingRef: RefObject<boolean>;
  shouldForceRenderRef: RefObject<boolean>;
  flaggedTrainIds?: string[];
}

export const useTrainAnimationLoop = ({
  mapExists,
  trainStateMapRef,
  visibleTrainIdsRef,
  visibleBoundsRef,
  currentZoomRef,
  lockedIdRef,
  isInteractingRef,
  shouldForceRenderRef,
  // flaggedTrainIds,
}: UseTrainAnimationLoopProps) => {
  const [trainData, setTrainData] = useState<TrainRenderData[]>([]);
  const [renderZoom, setRenderZoom] = useState(0);
  const [renderLockedId, setRenderLockedId] = useState<string | number | null>(null);

  const poolRef = useRef<TrainRenderData[]>([]);
  const stoppedGroupsRef = useRef<Map<number, number[]>>(new Map());
  const requestRef = useRef<number | null>(null);
  const lastRenderTimeRef = useRef(0);
  const hasRenderedDataRef = useRef(false);
  const frameCounterRef = useRef(0);
  const renderZoomRef = useRef(0);
  const renderLockedIdRef = useRef<string | number | null>(null);

  // const flaggedIdsKey = flaggedTrainIds?.join(',');

  // const flaggedSet = useMemo(() => {
  //   return flaggedIdsKey ? new Set(flaggedIdsKey.split(',')) : null;
  // }, [flaggedIdsKey]);

  const layers = useMemo<Layer[]>(() => {
    if (trainData.length === 0) {
      return [];
    }

    return getTrainDeckLayers(trainData, renderZoom, renderLockedId /*, flaggedSet*/);
  }, [trainData, renderZoom, renderLockedId /*, flaggedSet*/]);

  useEffect(() => {
    if (!mapExists) {
      return;
    }

    const animate = () => {
      requestRef.current = requestAnimationFrame(animate);

      const isInteracting = isInteractingRef.current;
      const visibleBounds = visibleBoundsRef.current;
      const currentZoom = currentZoomRef.current ?? 0;
      const shouldForce = shouldForceRenderRef.current;
      const lockedId = lockedIdRef.current;
      const trainStateMap = trainStateMapRef.current;
      const visibleTrainIds = visibleTrainIdsRef.current;

      const hasLockedTrain = !!lockedId;

      if (
        (isInteracting && !hasLockedTrain) ||
        !visibleBounds ||
        !trainStateMap ||
        !visibleTrainIds
      ) {
        return;
      }

      const now = Date.now();
      const dynamicInterval = getTrainRenderInterval(currentZoom);
      const timeSinceLastUpdate = now - lastRenderTimeRef.current;

      if (!shouldForce && timeSinceLastUpdate < dynamicInterval) {
        return;
      }

      lastRenderTimeRef.current = now;

      shouldForceRenderRef.current = false;

      const nextZoomBucket = getNextZoomBucket(currentZoom);

      if (nextZoomBucket !== renderZoomRef.current) {
        renderZoomRef.current = nextZoomBucket;
        setRenderZoom(nextZoomBucket);
      }

      if (lockedId !== renderLockedIdRef.current) {
        renderLockedIdRef.current = lockedId;
        setRenderLockedId(lockedId);
      }

      const activeCount = processTrainFrame({
        now,
        visibleTrainIds,
        trainStateMap,
        pool: poolRef.current,
        bounds: visibleBounds,
        lockedId,
        stoppedGroups: stoppedGroupsRef.current,
        zoom: currentZoom,
      });

      frameCounterRef.current += 1;

      if (frameCounterRef.current % POOL_CLEANUP_INTERVAL_FRAMES === 0) {
        const pool = poolRef.current;
        const targetSize = activeCount + POOL_BUFFER_SIZE;

        if (pool.length > activeCount * 2 && pool.length > MIN_POOL_CAPACITY) {
          pool.length = targetSize;
        }
      }

      if (activeCount > 0 || hasRenderedDataRef.current) {
        const currentData = poolRef.current.slice(0, activeCount);

        setTrainData(currentData);
        hasRenderedDataRef.current = activeCount > 0;
      }
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [mapExists]);

  return { layers, trainData };
};
